/**
 * Camera: fixed 32 px per unit, follows the local player with 25% lookahead
 * toward the cursor, clamped to the map (DESIGN 6.2). Dead players get a
 * free camera (`free = true`; `pan` moves it). Screen shake is an amplitude
 * that decays over its duration, capped at FEEL.shake.maxPx; the
 * reduce-motion flag zeroes it.
 *
 * Screen coordinates are CSS pixels; the renderer applies the device pixel
 * ratio with one setTransform per frame. `sx`/`sy` are the allocation-free
 * forms the hot layers use; `worldToScreen` / `screenToWorld` return fresh
 * Vec2s for callers outside the frame loop (Controls).
 */

import { ARENA, CLIENT, UNIT_PX, type Vec2 } from '@shared/index.js';
import { FEEL, easeK } from './feel.js';
import { noise1D } from './mathfx.js';

/** Units of arena allowed to scroll past the walls so the boundary wall and void stay visible. */
const EDGE_MARGIN = 1.0;
/** Lookahead is clamped to this many units so a far cursor does not leave the wizard at the screen edge. */
const LOOKAHEAD_MAX = 4;
/** Free-camera pan speed in units per second. */
export const FREE_PAN_SPEED = 14;

export class Camera {
  /** Pixels per world unit (fixed, DESIGN 6.2). */
  readonly scale: number = UNIT_PX;
  /** Size of the map the view is kept inside (the room's level; the arena until told otherwise). */
  boundsW: number = ARENA.w;
  boundsH: number = ARENA.h;
  /** World position at the centre of the viewport. */
  x: number = ARENA.w / 2;
  y: number = ARENA.h / 2;
  viewW = 1;
  viewH = 1;
  /** Current shake offset in CSS px, applied by `sx`/`sy`. */
  shakeX = 0;
  shakeY = 0;
  /** True while the local player is dead: `follow` is ignored and `pan` moves the camera. */
  free = false;
  reduceMotion = false;

  private shakeAmp = 0;
  private shakeT = 0;
  private shakeDur = 0;
  private time = 0;
  private snapped = false;
  /** The eased cursor lookahead, in world units. */
  private lax = 0;
  private lay = 0;

  resize(w: number, h: number): void {
    this.viewW = Math.max(1, w);
    this.viewH = Math.max(1, h);
    this.clamp();
  }

  /** Keep the view inside a map of `w` x `h` units (a new level). */
  setBounds(w: number, h: number): void {
    this.boundsW = w;
    this.boundsH = h;
    this.clamp();
  }

  /** World x -> screen x (CSS px) including shake. */
  sx(wx: number): number {
    return (wx - this.x) * this.scale + this.viewW * 0.5 + this.shakeX;
  }

  /** World y -> screen y (CSS px) including shake. */
  sy(wy: number): number {
    return (wy - this.y) * this.scale + this.viewH * 0.5 + this.shakeY;
  }

  worldToScreen(x: number, y: number): Vec2 {
    return { x: this.sx(x), y: this.sy(y) };
  }

  screenToWorld(px: number, py: number): Vec2 {
    return {
      x: (px - this.viewW * 0.5 - this.shakeX) / this.scale + this.x,
      y: (py - this.viewH * 0.5 - this.shakeY) / this.scale + this.y,
    };
  }

  /** Visible world bounds (for culling), with a half-unit slack. */
  get left(): number { return this.x - this.viewW / (2 * this.scale) - 0.5; }
  get right(): number { return this.x + this.viewW / (2 * this.scale) + 0.5; }
  get top(): number { return this.y - this.viewH / (2 * this.scale) - 0.5; }
  get bottom(): number { return this.y + this.viewH / (2 * this.scale) + 0.5; }

  /**
   * Follow `target` with CLIENT.cameraLookahead (25%) of the offset toward
   * `cursor`, clamped to LOOKAHEAD_MAX units. The lookahead eases on its own
   * (FEEL.camera.lookaheadRate) and the follow on top of it
   * (FEEL.camera.followRate), so a flick of the cursor leans the view rather
   * than yanking it. The first call snaps so a fresh join does not pan
   * across the arena.
   */
  follow(target: Vec2, cursor: Vec2, dt: number): void {
    if (this.free) return;
    let lx = (cursor.x - target.x) * CLIENT.cameraLookahead;
    let ly = (cursor.y - target.y) * CLIENT.cameraLookahead;
    const l = Math.hypot(lx, ly);
    if (l > LOOKAHEAD_MAX) {
      lx *= LOOKAHEAD_MAX / l;
      ly *= LOOKAHEAD_MAX / l;
    }
    if (!this.snapped) {
      this.lax = lx;
      this.lay = ly;
      this.x = target.x + lx;
      this.y = target.y + ly;
      this.snapped = true;
    } else {
      const kl = easeK(FEEL.camera.lookaheadRate, dt);
      this.lax += (lx - this.lax) * kl;
      this.lay += (ly - this.lay) * kl;
      const k = easeK(FEEL.camera.followRate, dt);
      this.x += (target.x + this.lax - this.x) * k;
      this.y += (target.y + this.lay - this.y) * k;
    }
    this.clamp();
  }

  /** Free-camera move in world units (dead players: arrows / edge pan). */
  pan(dx: number, dy: number): void {
    this.x += dx;
    this.y += dy;
    this.clamp();
  }

  /** Instant reposition (room join, respawn). */
  jumpTo(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.snapped = true;
    this.clamp();
  }

  /**
   * Screen shake: amplitude `px` (capped at FEEL.shake.maxPx) decaying to 0
   * over `seconds`. A new call never weakens a stronger shake in progress.
   */
  shake(px: number, seconds: number): void {
    if (this.reduceMotion) return;
    const amp = Math.min(FEEL.shake.maxPx, Math.max(0, px));
    if (amp >= this.shakeAmp) {
      this.shakeAmp = amp;
      this.shakeDur = Math.max(0.05, seconds);
      this.shakeT = this.shakeDur;
    }
  }

  /** Advance shake; call once per frame before drawing. */
  update(dt: number): void {
    this.time += dt;
    if (this.shakeT > 0 && !this.reduceMotion) {
      this.shakeT = Math.max(0, this.shakeT - dt);
      const f = this.shakeT / this.shakeDur;
      const a = this.shakeAmp * f * f;
      this.shakeX = a * noise1D(this.time * 37);
      this.shakeY = a * noise1D(this.time * 37 + 100);
      if (this.shakeT === 0) this.shakeAmp = 0;
    } else {
      this.shakeX = 0;
      this.shakeY = 0;
    }
  }

  /**
   * Keep the view inside the map plus EDGE_MARGIN. When the viewport is
   * wider/taller than the map in an axis, centre the map in that axis.
   */
  private clamp(): void {
    const bw = this.boundsW, bh = this.boundsH;
    const halfW = this.viewW / (2 * this.scale);
    const halfH = this.viewH / (2 * this.scale);
    const minX = -EDGE_MARGIN + halfW;
    const maxX = bw + EDGE_MARGIN - halfW;
    const minY = -EDGE_MARGIN + halfH;
    const maxY = bh + EDGE_MARGIN - halfH;
    this.x = minX > maxX ? bw / 2 : this.x < minX ? minX : this.x > maxX ? maxX : this.x;
    this.y = minY > maxY ? bh / 2 : this.y < minY ? minY : this.y > maxY ? maxY : this.y;
  }
}
